import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, EmpAuthProvider, useAuth, useEmpAuth } from "@/lib/auth";
import { LanguageProvider } from "@/lib/language";
import { BrandingProvider } from "@/lib/branding";
import PreviewEnvironmentGuard from "@/components/PreviewEnvironmentGuard";

const EmployeeLoginPage = lazy(() => import("@/pages/EmployeeLoginPage"));
const DashboardLayout = lazy(() => import("@/components/layout/DashboardLayout"));
const AdminHomePage = lazy(() => import("@/pages/AdminHomePage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const EmployeesPage = lazy(() => import("@/pages/EmployeesPage"));
const EmployeeDetailPage = lazy(() => import("@/pages/EmployeeDetailPage"));
const AttendancePage = lazy(() => import("@/pages/AttendancePage"));
const PayrollPage = lazy(() => import("@/pages/PayrollPage"));
const AlertsPage = lazy(() => import("@/pages/AlertsPage"));
const LeaveManagementPage = lazy(() => import("@/pages/LeaveManagementPage"));
const ShiftSchedulePage = lazy(() => import("@/pages/ShiftSchedulePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const UserManagementPage = lazy(() => import("@/pages/UserManagementPage"));
const AuditTrailPage = lazy(() => import("@/pages/AuditTrailPage"));
const EmployeePortalPage = lazy(() => import("@/pages/EmployeePortalPage"));
// New SOFT HR pages
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const OvertimePage = lazy(() => import("@/pages/OvertimePage"));
const OrganogramPage = lazy(() => import("@/pages/OrganogramPage"));
const VacanciesPage = lazy(() => import("@/pages/VacanciesPage"));
const OnCallPage = lazy(() => import("@/pages/OnCallPage"));
const BenefitsPage = lazy(() => import("@/pages/BenefitsPage"));
const SalaryGradesPage = lazy(() => import("@/pages/SalaryGradesPage"));
const LeaveBalancesPage = lazy(() => import("@/pages/LeaveBalancesPage"));
const DeptPayConfigPage = lazy(() => import("@/pages/DeptPayConfigPage"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const PaymentChannelsPage = lazy(() => import("@/pages/PaymentChannelsPage"));
const GeneralDocumentsPage = lazy(() => import("@/pages/GeneralDocumentsPage"));
const TaxPage = lazy(() => import("@/pages/TaxPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const AppointmentsPage = lazy(() => import("@/pages/AppointmentsPage"));
const OrdersPage = lazy(() => import("@/pages/OrdersPage"));
const InternalOrdersPage = lazy(() => import("@/pages/InternalOrdersPage"));
const ArchivePage = lazy(() => import("@/pages/ArchivePage"));
const DepartmentVisualsPage = lazy(() => import("@/pages/DepartmentVisualsPage"));
const BadgeEditorPage = lazy(() => import("@/pages/BadgeEditorPage"));
const MedicalEmergencyListPage = lazy(() => import("@/pages/MedicalEmergencyListPage"));
const BadgePrintPage = lazy(() => import("@/pages/BadgePrintPage"));
const DiagnosticsPage = lazy(() => import("@/pages/DiagnosticsPage"));
const DiagnosticsHubPage = lazy(() => import("@/pages/DiagnosticsHubPage"));
const PushDiagnosticsPage = lazy(() => import("@/pages/PushDiagnosticsPage"));
const LoginDesignerPage = lazy(() => import("@/pages/LoginDesignerPage"));
const OvertimeApprovalsPage = lazy(() => import("@/pages/OvertimeApprovalsPage"));
const AdminUsersPage = lazy(() => import("@/pages/AdminUsersPage"));
const ShiftTypesPage = lazy(() => import("@/pages/ShiftTypesPage"));
const SchedulingHubPage = lazy(() => import("@/pages/SchedulingHubPage"));
const SecurityPoliciesPage = lazy(() => import("@/pages/SecurityPoliciesPage"));
const DepartmentIntegrityPage = lazy(() => import("@/pages/DepartmentIntegrityPage"));
const EmailPoliciesPage = lazy(() => import("@/pages/EmailPoliciesPage"));
const AdminDiagnosticsPage = lazy(() => import("@/pages/AdminDiagnosticsPage"));
// GpsMapPage was removed Feb 2026 — see /app/memory/PRD.md (Privacy Hardening).
const QrProfilePage = lazy(() => import("@/pages/QrProfilePage"));

// ── Experimental workspace prototype (isolated, no production impact) ──
const WorkspaceHomePage = lazy(() => import("@/labs/workspace/WorkspaceHomePage"));
const WorkspacePage = lazy(() => import("@/labs/workspace/WorkspacePage"));
const WorkspaceSubmodulePage = lazy(() => import("@/labs/workspace/WorkspaceSubmodulePage"));
const WorkspaceEditorPage = lazy(() => import("@/labs/workspace/WorkspaceEditorPage"));
const ImagingWorklistPage = lazy(() => import("@/pages/ImagingWorklistPage"));
const ImagingModalitiesPage = lazy(() => import("@/pages/ImagingModalitiesPage"));
const ImagingReportEditorPage = lazy(() => import("@/pages/ImagingReportEditorPage"));
const ImagingSignaturesPage = lazy(() => import("@/pages/ImagingSignaturesPage"));
const ImagingTemplatesPage = lazy(() => import("@/pages/ImagingTemplatesPage"));
const ImagingReportVerifyPage = lazy(() => import("@/pages/ImagingReportVerifyPage"));
const ImagingSandboxPage = lazy(() => import("@/pages/ImagingSandboxPage"));
const ImagingDemoViewerPage = lazy(() => import("@/pages/ImagingDemoViewerPage"));

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PortalRoute({ children }) {
  const { employee, loading } = useEmpAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!employee) return <Navigate to="/login" replace />;
  return children;
}

function Loader() {
  return <div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
}

export default function App() {
  // Clean up old localStorage auth tokens from pre-cookie migration
  React.useEffect(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("emp_token");
    localStorage.removeItem("emp_user");
  }, []);

  return (
    <LanguageProvider>
      <BrandingProvider>
        <AuthProvider>
          <EmpAuthProvider>
            <PreviewEnvironmentGuard>
            <BrowserRouter>
            <Toaster
              position="top-right"
              closeButton
              expand={false}
              gap={8}
              toastOptions={{
                duration: 3500,
                classNames: {
                  toast: "group rounded-xl border border-zinc-200 bg-white text-zinc-800 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.15)]",
                  title: "text-[13px] font-medium",
                  description: "text-[12px] text-zinc-500",
                  icon: "text-zinc-400",
                  closeButton: "border-zinc-200 bg-white text-zinc-500 hover:text-zinc-900",
                  success: "[&_[data-icon]]:!text-emerald-600",
                  error: "[&_[data-icon]]:!text-red-600",
                  warning: "[&_[data-icon]]:!text-amber-600",
                  info: "[&_[data-icon]]:!text-blue-600",
                },
              }}
            />
            <Suspense fallback={<Loader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<EmployeeLoginPage />} />
                <Route path="/qr/:token" element={<QrProfilePage />} />
                <Route path="/badge-print/:id" element={<BadgePrintPage />} />
                {/* Public share routes — read-only via signed share token */}
                <Route path="/r/medical" element={<MedicalEmergencyListPage />} />
                <Route path="/r/imaging-verify/:token" element={<ImagingReportVerifyPage />} />
                <Route path="/portal/login" element={<Navigate to="/login" replace />} />
                <Route path="/portal" element={<PortalRoute><EmployeePortalPage /></PortalRoute>} />
                <Route path="/portal/organogram" element={<PortalRoute><OrganogramPage /></PortalRoute>} />
                <Route path="/dashboard" element={<AdminRoute><AdminHomePage /></AdminRoute>} />
                <Route path="/reports" element={<AdminRoute><ReportsPage /></AdminRoute>} />
                {/* ── Experimental workspace prototype (zero-impact, no chrome) ── */}
                <Route path="/lab/workspace" element={<AdminRoute><WorkspaceHomePage /></AdminRoute>} />
                <Route path="/lab/workspace/editor" element={<AdminRoute><WorkspaceEditorPage /></AdminRoute>} />
                <Route path="/lab/workspace/:workspaceId" element={<AdminRoute><WorkspacePage /></AdminRoute>} />
                <Route path="/lab/workspace/:workspaceId/:submoduleId" element={<AdminRoute><WorkspaceSubmodulePage /></AdminRoute>} />
                <Route element={<AdminRoute><DashboardLayout /></AdminRoute>}>
                  <Route path="/employees" element={<EmployeesPage />} />
                  <Route path="/employees/:id" element={<EmployeeDetailPage />} />
                  <Route path="/attendance" element={<AttendancePage />} />
                  <Route path="/payroll" element={<PayrollPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/leave-management" element={<LeaveManagementPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  {/* New SOFT HR routes */}
                  <Route path="/documents" element={<DocumentsPage />} />
                  <Route path="/overtime" element={<OvertimePage />} />
                  <Route path="/organogram" element={<OrganogramPage />} />
                  <Route path="/vacancies" element={<VacanciesPage />} />
                  <Route path="/oncall" element={<OnCallPage />} />
                  <Route path="/benefits" element={<BenefitsPage />} />
                  <Route path="/salary-grades" element={<SalaryGradesPage />} />
                  <Route path="/leave-balances" element={<LeaveBalancesPage />} />
                  <Route path="/general-documents" element={<GeneralDocumentsPage />} />
                  <Route path="/tax" element={<TaxPage />} />
                  <Route path="/marketing" element={<MarketingPage />} />
                  <Route path="/appointments" element={<AppointmentsPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/internal-orders" element={<InternalOrdersPage />} />
                  <Route path="/shift-schedule" element={<Navigate to="/scheduling?tab=weekly" replace />} />
                  <Route path="/scheduling" element={<SchedulingHubPage />} />
                  <Route path="/security-policies" element={<SecurityPoliciesPage />} />
                  <Route path="/department-integrity" element={<DepartmentIntegrityPage />} />
                  <Route path="/email-policies" element={<EmailPoliciesPage />} />
                  <Route path="/admin-diagnostics" element={<AdminDiagnosticsPage />} />
                  <Route path="/archive" element={<ArchivePage />} />
                  <Route path="/user-management" element={<UserManagementPage />} />
                  <Route path="/contracts" element={<ContractsPage />} />
                  <Route path="/dept-pay" element={<DeptPayConfigPage />} />
                  <Route path="/payment-channels" element={<PaymentChannelsPage />} />
                  <Route path="/department-visuals" element={<DepartmentVisualsPage />} />
                  <Route path="/badge-editor" element={<BadgeEditorPage />} />
                  <Route path="/reports/medical" element={<MedicalEmergencyListPage />} />
                  <Route path="/imaging/worklist" element={<ImagingWorklistPage />} />
                  <Route path="/imaging/modalities" element={<ImagingModalitiesPage />} />
                  <Route path="/imaging/templates" element={<ImagingTemplatesPage />} />
                  <Route path="/imaging/signatures" element={<ImagingSignaturesPage />} />
                  <Route path="/imaging/reports/:reportId" element={<ImagingReportEditorPage />} />
                  <Route path="/imaging/sandbox" element={<ImagingSandboxPage />} />
                  <Route path="/imaging/viewer/:studyId" element={<ImagingDemoViewerPage />} />
                  <Route path="/diagnostics" element={<DiagnosticsHubPage />} />
                  <Route path="/push-diagnostics" element={<Navigate to="/diagnostics?tab=push" replace />} />
                  <Route path="/login-designer" element={<LoginDesignerPage />} />
                  <Route path="/overtime-approvals" element={<OvertimeApprovalsPage />} />
                  <Route path="/admin-users" element={<AdminUsersPage />} />
                  <Route path="/shift-types" element={<Navigate to="/scheduling?tab=shift-types" replace />} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
          </PreviewEnvironmentGuard>
        </EmpAuthProvider>
      </AuthProvider>
      </BrandingProvider>
    </LanguageProvider>
  );
}
